import { useLayoutEffect, useRef, type ReactNode } from 'react';

export interface TemplateTextEditorProps {
  value: string;
  tokens: readonly { value: string; label: string; disabled?: boolean }[];
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel: string;
  tokenGroupLabel: string;
  maxLength?: number;
  onChange?: (event: { value: string }) => void;
}

/** Tokens are literal text, never evaluated as templates. */
export function TemplateTextEditor({ value, tokens, disabled = false, readOnly = false, ariaLabel, tokenGroupLabel, maxLength, onChange }: TemplateTextEditorProps): ReactNode {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const composing = useRef(false);
  const pending = useRef<{ previous: string; value: string; caret: number }>();
  useLayoutEffect(() => {
    const request = pending.current;
    if (!request) return;
    if (disabled || readOnly || (value !== request.previous && value !== request.value)) {
      pending.current = undefined;
    } else if (value === request.value) {
      pending.current = undefined;
      textarea.current?.focus();
      textarea.current?.setSelectionRange(request.caret, request.caret);
    }
  }, [value, disabled, readOnly]);
  useLayoutEffect(() => () => { pending.current = undefined; }, []);
  const fits = (next: string) => maxLength === undefined || next.length <= maxLength;
  return <div>
    <textarea ref={textarea} value={value} disabled={disabled} readOnly={readOnly} aria-label={ariaLabel} maxLength={maxLength}
      onCompositionStart={() => { composing.current = true; pending.current = undefined; }}
      onCompositionEnd={() => { composing.current = false; }}
      onChange={event => {
        pending.current = undefined;
        if (!disabled && !readOnly && fits(event.currentTarget.value)) onChange?.({ value: event.currentTarget.value });
      }}
    />
    <div role="group" aria-label={tokenGroupLabel}>{tokens.map((token, index) => <button key={index} type="button" disabled={disabled || readOnly || token.disabled}
      onMouseDown={event => event.preventDefault()}
      onClick={() => {
        const input = textarea.current;
        if (!input || disabled || readOnly || token.disabled || composing.current || pending.current) return;
        const start = input.selectionStart, end = input.selectionEnd;
        const next = value.slice(0, start) + token.value + value.slice(end);
        if (!fits(next) || next === value) return;
        pending.current = { previous: value, value: next, caret: start + token.value.length };
        onChange?.({ value: next });
      }}
    >{token.label}</button>)}</div>
  </div>;
}
